import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { ClientSideRowModelModule } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { CheckCircle, WarningAmber } from '@mui/icons-material';
import { dimension } from "utils";
import { useBDS } from "hooks";
import { width } from "@mui/system";


// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);


const statusColors: Record<string, "success" | "error" | "warning"> = {
  Approved: "success",
  Rejected: "error",
  Pending: "warning",
};

export const columnHouseDefs = [
  { field: "NO", headerName: "NO.", width: 70 },
  { field: "物件名", headerName: "物件名", minWidth: 30 },
  { field: "価格(万円)", headerName: "価格(万円)", minWidth: 30 },
  { field: "土地面積", headerName: "土地面積", minWidth: 30 },
  { field: "周辺道路", headerName: "周辺道路", minWidth: 30 },
  { field: "住所", headerName: "住所", minWidth: 30 },
  { field: "間取り", headerName: "間取り", minWidth: 30 },
  { field: "建物面積", headerName: "建物面積", minWidth: 30 },
  { field: "築年月", headerName: "築年月", minWidth: 30 },
  { field: "用途地域", headerName: "用途地域", minWidth: 30 },
  { field: "リフォーム履歴", headerName: "リフォーム履歴", minWidth: 30 },
  { field: "所在階/構造・階建", headerName: "所在階/構造・階建", minWidth: 30 },
  { field: "駐車台数", headerName: "駐車台数", minWidth: 30 },
  { field: "エレベーター", headerName: "エレベーター", minWidth: 30 },
  { field: "ガス等", headerName: "ガス等", minWidth: 30 },
  {
    field: "売買状況(0:未,1:成立)", headerName: "売買状況(0:未,1:成立)", minWidth: 30,
    cellRenderer: (params: any) => {
      return params.value === 1 ?
        <CheckCircle color="success" />
        : <WarningAmber color="warning" />
    },
  },
  {
    field: "物件URL", headerName: "物件URL", minWidth: 30,
    cellRenderer: (params: any) => {
      return params.value !== '' ?
        <a href={params.value} target="_blank" rel="noreferrer">{params.value}</a>
        : params.value
    },
  },
]

export const columnDefs = [
  { field: "NO", headerName: "NO.", width: 70 },
  { field: "物件名", headerName: "物件名", vi: "Tên tài sản", field1: "property-name", minWidth: 30 },
  { field: "価格(万円)", headerName: "価格(万円)", vi: "Giá (triệu yên)", field1: "price", minWidth: 20 },
  { field: "専有面積", headerName: "専有面積", vi: "Diện tích riêng biệt (m2)", field1: "exclusive-area", minWidth: 50 },
  { field: "所在階/構造・階建", headerName: "所在階/構造・階建", vi: "Kết cấu", field1: "structure", minWidth: 30 },
  { field: "住所", headerName: "住所", vi: "Địa chỉ", field1: "address", minWidth: 30 },
  { field: "間取り", headerName: "間取り", vi: "Mặt bằng", field1: "layout", minWidth: 30 },
  { field: "総戸数", headerName: "総戸数", vi: "Tổng số đơn vị", field1: "total-units", minWidth: 30 },
  { field: "その他面積", headerName: "その他面積", vi: "Khu vực khác", field1: "other-area", minWidth: 30 },
  { field: "築年月", headerName: "築年月", vi: "Tuổi xây dựng", field1: "building-age", minWidth: 30 },
  { field: "修繕積立金", headerName: "修繕積立金", vi: "Quỹ dự phòng sửa chữa (triệu yên)", field1: "repair-reserve-fund", minWidth: 30 },
  { field: "管理費", headerName: "管理費", vi: "Phí quản lý (/tháng)", field1: "management-fee", minWidth: 30 },
  { field: "諸費用", headerName: "諸費用", vi: "Các khoản phí khác", field1: "miscellaneous-fees", minWidth: 30 },
  { field: "方位", headerName: "方位", vi: "Phương hướng", field1: "orientation", minWidth: 30 },
  { field: "リフォーム履歴", headerName: "リフォーム履歴", vi: "Lịch sử cải tạo", field1: "renovation-history", minWidth: 30 },
  { field: "用途地域", headerName: "用途地域", vi: "Phân vùng", field1: "zoning", minWidth: 30 },
  { field: "駐車場料金", headerName: "駐車場料金", vi: "Phí đỗ xe", field1: "parking-fee", minWidth: 30 },
  { field: "駐車台数", headerName: "駐車台数", vi: "Số lượng chỗ đậu xe", field1: "number-of-parking-spaces", minWidth: 30 },
  { field: "エレベーター", headerName: "エレベーター", vi: "Khả năng sử dụng thang máy", field1: "elevator-availability", minWidth: 30 },
  { field: "ガス等", headerName: "ガス等", vi: "Khí đốt", field1: "gas-and-utilities", minWidth: 30 },
  {
    field: "売買状況(0:未,1:成立)", headerName: "売買状況(0:未,1:成立)", vi: "Trạng thái giao dịch (0: Chưa, 1: Thành công)", field1: "transaction-status", minWidth: 30,
    cellRenderer: (params: any) => {
      return params.value === 1 ?
        <CheckCircle color="success" />
        : <WarningAmber color="warning" />
    },
  },
  {
    field: "物件URL", headerName: "物件URL", vi: "URL thuộc tính", field1: "property-url", minWidth: 30,
    cellRenderer: (params: any) => {
      return params.value !== '' ?
        <a href={params.value} target="_blank" rel="noreferrer">{params.value}</a>
        : params.value
    },
  },
  // { field: "カテゴリ", headerName: "カテゴリ", vi: "Loại", field1: "type",minWwidth:30 },
];

const TableDemo = ({ ptype, changeStatus }) => {
  const prevRef = useRef<any>()
  const [rowData, setRowData] = useState([]);
  const { onGetData } = useBDS()

  useEffect(() => {
    onGetData(ptype, data => {
      prevRef.current = data
      setRowData(data)
    });
  }, [ptype]);

  useEffect(() => {
    if (!prevRef.current) return;
    // onGetData(ptype, data => setRowData(data));
    switch (changeStatus) {
      case "0":
      case "1":
      case "2":
        const rs = prevRef.current?.filter(e => e['売買状況(0:未,1:成立)'] == changeStatus);
        setRowData(rs)
        break;

      default:
        setRowData(prevRef.current)
        break;
    }
  }, [changeStatus]);

  return (
    <Box>
      <div className="ag-theme-alpine" style={{ height: 700, width: "100%" }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={ptype === 'house' ? columnHouseDefs : columnDefs}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: false,
            tooltipComponent: true
          }}
          modules={[ClientSideRowModelModule]}
          rowModelType="clientSide"
        />
      </div>
    </Box>
  );
};

export default TableDemo;
